/**
 * ServiceCatalogTable - displays services in a data table
 */
import { Globe } from 'lucide-react'
import { DataTable, type ColumnDef } from '@/components/data-display/DataTable'
import { Badge } from '@/components/ui/badge'
import type { ServiceSummary } from '@/lib/types'

interface ServiceCatalogTableProps {
  /** Services to display */
  services: ServiceSummary[]
  /** Whether data is loading */
  isLoading?: boolean
  /** Called when a service row is clicked */
  onServiceClick?: (service: ServiceSummary) => void
}

/**
 * Table for displaying service catalog entries.
 * Shows service name, description, owner team, area path, and tech stack.
 */
export function ServiceCatalogTable({
  services,
  isLoading,
  onServiceClick,
}: ServiceCatalogTableProps) {
  const columns: ColumnDef<ServiceSummary>[] = [
    {
      id: 'name',
      header: 'Service',
      sortable: true,
      className: 'font-medium',
      cell: (service) => (
        <div className="flex flex-col">
          <span className="font-medium">{service.name}</span>
          {(service.aliases?.length ?? 0) > 0 && (
            <span className="text-xs text-muted-foreground">
              aka {service.aliases!.slice(0, 2).join(', ')}
              {service.aliases!.length > 2 && ` +${service.aliases!.length - 2} more`}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'country',
      header: 'Country',
      sortable: true,
      cell: (service) => (
        <span className="text-sm text-muted-foreground inline-flex items-center gap-1">
          <Globe className="h-3 w-3" />
          {service.country || 'Global'}
        </span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      className: 'max-w-md',
      cell: (service) => (
        <span className="text-sm text-muted-foreground line-clamp-2">
          {service.description || '—'}
        </span>
      ),
    },
    {
      id: 'ownerTeam',
      header: 'Owner Team',
      sortable: true,
      cell: (service) => (
        <span className="text-sm text-muted-foreground">
          {service.ownerTeam ?? '—'}
        </span>
      ),
    },
    {
      id: 'areaPath',
      header: 'Area Path',
      sortable: true,
      cell: (service) => (
        <span className="text-sm font-mono text-muted-foreground">
          {service.areaPath?.split('\\').pop() ?? '—'}
        </span>
      ),
    },
    {
      id: 'techStack',
      header: 'Tech Stack',
      cell: (service) => {
        const stack = service.techStack ?? []
        if (stack.length === 0) {
          return <span className="text-sm text-muted-foreground">—</span>
        }
        return (
          <div className="flex flex-wrap gap-1">
            {stack.slice(0, 3).map((tech) => (
              <Badge key={tech} variant="secondary" className="text-xs">
                {tech}
              </Badge>
            ))}
            {stack.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{stack.length - 3}
              </Badge>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <DataTable
      data={services}
      columns={columns}
      getRowKey={(service) => service.name}
      onRowClick={onServiceClick}
      isLoading={isLoading}
      emptyMessage="No services found. Try adjusting your search or filters."
    />
  )
}
